import { Button } from "@/components/ui/button";
import { MenuBar } from "@/components/ui/glow-menu";
import { useAnalyticsTracker } from "@/hooks/useAnalyticsTracker";
import { trackEvent } from "@/utils/analytics";
import { BookOpen, Building, FileText, Mail, Star } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import ChatWidget from './chat/ChatWidget';
import Footer from './Footer';
import { useTheme } from './theme-provider';
import ThemeToggle from './theme-toggle';

/**
 * Configuração dos itens do menu principal
 * Cada item contém:
 * - icon: Ícone do Lucide a ser exibido
 * - label: Texto do item de menu
 * - href: Link de destino (# para seções na página inicial)
 * - gradient: Estilo do gradiente de fundo
 * - iconColor: Cor do ícone
 */
export const menuItems = [
  {
    icon: FileText,
    label: "Serviços",
    href: "/#services",
    gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)",
    iconColor: "text-blue-500",
  },
  {
    icon: Building,
    label: "Sobre",
    href: "/#about",
    gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)",
    iconColor: "text-blue-500",
  },
  {
    icon: Star,
    label: "Referências",
    href: "/#testimonials",
    gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)",
    iconColor: "text-blue-500",
  },
  {
    icon: Mail,
    label: "Contato",
    href: "/#contact",
    gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)",
    iconColor: "text-blue-500",
  },
  {
    icon: BookOpen,
    label: "Blog",
    href: "/blog",
    gradient: "radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, rgba(29,78,216,0) 100%)",
    iconColor: "text-blue-500",
  },
];

/**
 * Interface para as props do componente Layout
 */
interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Componente Layout - Estrutura principal do site
 * Inclui cabeçalho, menu de navegação, conteúdo principal e rodapé
 * Também gerencia a navegação entre páginas e seções
 */
const Layout = ({ children }: LayoutProps) => {
  const { theme } = useTheme();
  // Estado para controlar a abertura/fechamento do menu mobile
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Estado para rastrear o item de menu ativo
  const [activeItem, setActiveItem] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { trackUserInteraction } = useAnalyticsTracker();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Rola para o topo quando a rota muda
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Define o item de menu ativo com base no caminho atual ou hash
  useEffect(() => {
    const path = location.pathname;
    const hash = location.hash;
    
    if (path === "/blog") {
      setActiveItem("Blog");
    } else if (path === "/" && hash) {
      const item = menuItems.find(item => item.href === `/#${hash.substring(1)}`);
      if (item) setActiveItem(item.label);
    } else if (path === "/") {
      setActiveItem("Serviços");
    }
  }, [location.pathname, location.hash]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Não fechar o menu se o clique foi no botão de toggle
      const target = event.target as HTMLElement;
      if (target.closest('button[aria-label="Close menu"]') || target.closest('button[aria-label="Open menu"]')) {
        return;
      }
      
      if (isMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Close mobile menu on window resize (if screen becomes large)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMenuOpen]);

  /**
   * Navega para uma seção específica da página
   * Se não estiver na página inicial, navega primeiro para a página inicial
   * @param sectionId - ID da seção para navegar (com #)
   */
  const handleNavigateToSection = (sectionId: string) => {
    const sectionName = sectionId.replace('#', '');
    trackEvent('Navigation', 'SectionScroll', `Section: ${sectionName}`);
    
    // Close mobile menu when navigating
    setIsMenuOpen(false);
    
    if (location.pathname !== "/") {
      // Se não estiver na página inicial, navega para a página inicial primeiro
      navigate("/");
      // Aguarda a navegação ser concluída
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Já está na página inicial, apenas rola para a seção
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  /**
   * Manipula o clique em um item do menu
   * @param label - Rótulo do item de menu clicado
   */
  const handleMenuClick = (label: string) => {
    trackEvent('Navigation', 'MenuClick', `Menu Item: ${label}`);
    setActiveItem(label);
    setIsMenuOpen(false);
    
    const item = menuItems.find(item => item.label === label);
    if (!item) return;
    
    if (item.href.startsWith("/#")) {
      // Para links de seção na página inicial
      const sectionId = item.href.substring(1);
      handleNavigateToSection(sectionId);
    } else {
      // Para outras páginas como Blog
      navigate(item.href);
    }
  };

  /**
   * Rola para a seção de contato
   */
  const scrollToContact = () => {
    trackEvent('Navigation', 'ScrollToContact', 'Header CTA');
    handleNavigateToSection("#contact");
    setActiveItem("Contato");
  };

  /**
   * Manipula o clique no logotipo
   * Fecha o menu mobile, navega para a página inicial e rola para o topo
   */
  const handleLogoClick = () => {
    trackEvent('Navigation', 'LogoClick', 'Return to Home');
    // Fecha o menu mobile se estiver aberto
    setIsMenuOpen(false);
    
    if (location.pathname !== "/") {
      // Se não estiver na página inicial, navega para a página inicial primeiro
      navigate("/", { state: { fromCanonical: true } });
      // Aguarda a navegação ser concluída antes de rolar suavemente para o topo
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } else {
      // Já está na página inicial, apenas rola suavemente para o topo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setActiveItem("Serviços");
  };

  /**
   * Manipula a navegação a partir do rodapé
   * @param e - Evento do React
   * @param href - URL de destino
   */
  const handleFooterNavigation = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    
    const destination = href === "/" 
      ? "Home" 
      : href === "/blog" 
        ? "Blog" 
        : `Section: ${href.replace('/#', '')}`;
        
    trackEvent('Navigation', 'FooterLink', `Destination: ${destination}`);
    
    if (href === "/") {
      navigate("/");
    } else if (href === "/blog") {
      navigate("/blog");
    } else if (href.startsWith("/#")) {
      const sectionId = href.substring(1);
      handleNavigateToSection(sectionId);
    }
  };

  /**
   * Manipula o clique no botão do menu mobile
   */
  const handleMobileMenuToggle = () => {
    trackEvent('Navigation', 'MobileMenuToggle', `Action: ${isMenuOpen ? 'Close' : 'Open'}`);
    setIsMenuOpen(prevState => !prevState);
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      {/* SEO e metadados - Structured Data para LocalBusiness e ProfessionalService */}
      <Helmet>
        <title>Moacyr Moura Ferreira - Perito Judicial e Assistente Técnico em Campinas-SP</title>
        <meta name="description" content="Engenheiro Civil especializado em Perícia Judicial e Assistência Técnica em Campinas-SP. Atendimento em vistorias técnicas, avaliações imobiliárias, laudos periciais e mais. Garantia de ética, privacidade e qualidade nos serviços prestados." />
        <meta name="author" content="Moacyr Moura Ferreira" />
        <meta name="keywords" content="Perito Judicial, Assistente Técnico, Engenheiro Civil, Campinas-SP, Vistorias Técnicas, Avaliações Imobiliárias, Laudos Periciais, Vistorias Locativas, Vistorias Cautelares, Recebimento de Obras" />
        <meta property="og:title" content="Moacyr Moura Ferreira - Perito Judicial e Assistente Técnico em Campinas-SP" />
        <meta property="og:description" content="Engenheiro Civil especializado em Perícia Judicial e Assistência Técnica em Campinas-SP. Atendimento em vistorias técnicas, avaliações imobiliárias, laudos periciais e mais. Garantia de ética, privacidade e qualidade nos serviços prestados." />
        <meta property="og:image" content="https://www.periciatecnica.eng.br/assets/images/og-image.png" />
        <meta property="og:url" content="https://www.periciatecnica.eng.br" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Moacyr Moura Ferreira - Perito Judicial e Assistente Técnico em Campinas-SP" />
        <meta name="twitter:description" content="Engenheiro Civil especializado em Perícia Judicial e Assistência Técnica em Campinas-SP. Atendimento em vistorias técnicas, avaliações imobiliárias, laudos periciais e mais." />
        {location.pathname === '/' ? (
          <link rel="canonical" href="https://www.periciatecnica.eng.br" />
        ) : location.pathname.startsWith('/blog/') ? (
          <link rel="canonical" href={`https://www.periciatecnica.eng.br${location.pathname}`} />
        ) : location.pathname === '/blog' ? (
          <link rel="canonical" href="https://www.periciatecnica.eng.br/blog" />
        ) : (
          <link rel="canonical" href="https://www.periciatecnica.eng.br" />
        )}
        <meta name="robots" content="index, follow" />
        {/* Schema.org JSON-LD para ProfessionalService */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              "name": "Moacyr Moura Ferreira - Perito Judicial",
              "image": "/assets/images/team/moacyr.jpg",
              "url": "https://www.periciatecnica.eng.br",
              "telephone": "+5519991133163",
              "email": "moacyr.mmf@gmail.com",
              "description": "Engenheiro Civil especializado em Perícia Judicial e Assistência Técnica em Campinas-SP. Atendimento em vistorias técnicas, avaliações imobiliárias, laudos periciais e mais.",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Campinas",
                "addressRegion": "SP",
                "addressCountry": "BR"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": -22.9099,
                "longitude": -47.0626
              },
              "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "18:00"
              },
              "sameAs": [
                "https://www.linkedin.com/in/moacyr-moura-ferreira-542565b"
              ],
              "priceRange": "$$",
              "areaServed": {
                "@type": "GeoCircle",
                "geoMidpoint": {
                  "@type": "GeoCoordinates",
                  "latitude": -22.9099,
                  "longitude": -47.0626
                },
                "geoRadius": "100000"
              },
              "serviceType": ["Perícia Judicial", "Assistência Técnica", "Vistorias Técnicas", "Avaliações Imobiliárias"]
            }
          `}
        </script>
        <link 
          rel="stylesheet" 
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
        />
      </Helmet>
      
      {/* Default meta tags that apply site-wide */}
      <Helmet defaultTitle="Perícia Técnica Eng. Moacyr Moura" titleTemplate="%s | Perícia Técnica Eng. Moacyr Moura" defer>
        <meta name="description" content="Serviços de perícia judicial e assistência técnica em engenharia civil. Vistorias, avaliações e laudos técnicos. Atendimento em Campinas-SP." />
        <meta property="og:description" content="Serviços de perícia judicial e assistência técnica em engenharia civil. Vistorias, avaliações e laudos técnicos." />
        <meta property="og:image" content="https://www.periciatecnica.eng.br/assets/images/og-image.png" />
        <meta name="twitter:description" content="Serviços de perícia judicial e assistência técnica em engenharia civil." />
      </Helmet>

      {/* Cabeçalho fixo com navegação */}
      <header className="fixed w-full z-50 glass-nav bg-white/90 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-300/50 dark:border-gray-800/50 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:container md:mx-auto md:max-w-7xl">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Nome */}
            <div className="flex items-center">
              <button 
                onClick={handleLogoClick}
                className="text-xl font-semibold text-blue-600 dark:text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 transition-colors text-left"
                aria-label="Home"
              >
                Moacyr Moura Ferreira
              </button>
            </div>
            
            {/* Botão do menu mobile */}
            <button 
              className="lg:hidden text-gray-900 dark:text-white ml-auto p-2 z-50 relative"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>

            {/* Menu de navegação - responsivo */}
            <div ref={mobileMenuRef} className="lg:static">
              <nav className={`${isMenuOpen ? 'block fixed top-16 left-0 right-0 glass-nav z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-200 dark:border-gray-800 w-full' : 'hidden'} lg:static lg:block lg:relative lg:top-0 lg:z-auto flex-1 lg:mx-8`}>
                <div className="p-4 pb-6 lg:p-0 max-h-[calc(100vh-4rem)] overflow-y-auto">
                  {/* Desktop menu */}
                  <div className="hidden lg:block">
                    <MenuBar
                      items={menuItems}
                      activeItem={activeItem}
                      onItemClick={handleMenuClick}
                      className="lg:w-auto justify-center"
                    />
                  </div>
                  
                  {/* Mobile menu - vertical layout */}
                  <div className="lg:hidden">
                    <ul className="flex flex-col space-y-3 px-2">
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = item.label === activeItem;
                        return (
                          <li key={item.label}>
                            <button
                              onClick={() => handleMenuClick(item.label)}
                              className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
                                isActive 
                                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium' 
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50'
                              }`}
                            >
                              <Icon className={`h-5 w-5 mr-3 ${isActive ? item.iconColor : 'text-gray-500 dark:text-gray-400'}`} />
                              <span>{item.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  
                  {/* Theme toggle for mobile - only visible when menu is open */}
                  <div className="mt-6 flex justify-center lg:hidden">
                    <ThemeToggle />
                  </div>
                  
                  {/* Mobile CTA button */}
                  <div className="mt-6 lg:hidden">
                    <Button 
                      variant="outline" 
                      className="w-full border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black dark:hover:text-white py-3"
                      onClick={() => {
                        handleNavigateToSection("#contact");
                        setIsMenuOpen(false);
                      }}
                    >
                      Solicitar Orçamento
                    </Button>
                  </div>
                </div>
              </nav>
            </div>

            {/* Botão CTA - visível apenas em desktop */}
            <div className="hidden lg:flex lg:items-center gap-4">
              <ThemeToggle />
              <Button 
                variant="outline" 
                className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black dark:hover:text-white"
                onClick={() => handleNavigateToSection("#contact")}
              >
                Solicitar Orçamento
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="flex-grow">
        {children}
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
};

export default Layout;
